'use client';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';

import 'react-calendar/dist/Calendar.css';

import classes from './AppointmentPicker.module.scss';
import { CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { gaClick, gaFormSubmit } from '@/src/helpers/gtag';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIME_ZONE = 'Europe/Paris';
const SLOT_MINUTES = 20;

const DAILY_CANDIDATE_SLOTS = [
  { hour: 9, minute: 0 },
  { hour: 11, minute: 0 },
  { hour: 14, minute: 0 },
  { hour: 16, minute: 30 },
  { hour: 18, minute: 0 },
];

function slotFitsRanges(start: dayjs.Dayjs, end: dayjs.Dayjs, ranges: TimeRangeValue[]) {
  return ranges.some((range) => {
    const rStart = dayjs(range.start).tz(TIME_ZONE);
    const rEnd = dayjs(range.end).tz(TIME_ZONE);
    return (
      (start.isSame(rStart) || start.isAfter(rStart)) && (end.isSame(rEnd) || end.isBefore(rEnd))
    );
  });
}

export interface TimeRangeValue {
  start: string;
  end: string;
}

type ValuePiece = Date | null;
type CalendarValue = ValuePiece | [ValuePiece, ValuePiece];

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= breakpoint);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

function formatTime(iso: string, lang: string) {
  return new Date(iso).toLocaleTimeString(lang === 'en' ? 'en-GB' : 'fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });
}

export function AppointmentPicker({ lang }: { lang: string }) {
  const isEn = lang === 'en';
  const isMobile = useIsMobile();

  const t = useMemo(
    () => ({
      dateLabel: isEn ? 'Pick a date' : 'Choisissez une date',
      slotsLabel: isEn ? 'Available slots' : 'Créneaux disponibles',
      morningLabel: isEn ? 'Morning' : 'Matin',
      afternoonLabel: isEn ? 'Afternoon' : 'Après-midi',
      eveningLabel: isEn ? 'Evening' : 'Soir',
      slotsPrompt: isEn
        ? 'Select a date to see available slots.'
        : 'Sélectionnez une date pour voir les créneaux disponibles.',
      noSlots: isEn ? 'No availability on this day.' : 'Aucune disponibilité ce jour.',
      nameLabel: isEn ? 'Name' : 'Nom',
      namePlaceholder: isEn ? 'Your name' : 'Votre nom',
      emailLabel: isEn ? 'Email' : 'Email',
      emailPlaceholder: isEn ? 'you@company.com' : 'vous@entreprise.com',
      companyLabel: isEn ? 'Company' : 'Société',
      companyPlaceholder: isEn ? 'Your company' : 'Votre société',
      messageLabel: isEn ? 'What do you want to deliver?' : 'Que souhaitez-vous livrer ?',
      messagePlaceholder: isEn
        ? 'An MVP, a business application, a scope to de-risk…'
        : 'Un MVP, une application métier, un lot à sécuriser…',
      submit: isEn ? 'Book appointment' : 'Réserver le créneau',
      submitting: isEn ? 'Booking…' : 'Réservation…',
      success: isEn
        ? 'Your appointment is booked. See you soon.'
        : 'Votre rendez-vous est réservé. À très bientôt.',
      error: isEn
        ? 'Could not book this slot. Please try again.'
        : 'Impossible de réserver ce créneau. Réessayez.',
      ndaNote: isEn
        ? 'Confidential project? We can sign your NDA before the call.'
        : 'Projet confidentiel ? Nous pouvons signer votre NDA avant l’échange.',
    }),
    [isEn]
  );

  const [activeMonth, setActiveMonth] = useState<Date>(new Date());
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [ranges, setRanges] = useState<TimeRangeValue[]>([]);
  const [selectedRange, setSelectedRange] = useState<TimeRangeValue | null>(null);
  const [_loadingDays, setLoadingDays] = useState(false);
  const [loadingRanges, setLoadingRanges] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    const month = dayjs(activeMonth).tz(TIME_ZONE).format('YYYY-MM');
    setLoadingDays(true);
    fetch(`/api/appointment/slots?month=${month}`)
      .then((res) => res.json())
      .then((data: { days?: string[] }) => {
        setAvailableDays(data.days ?? []);
      })
      .catch(() => setAvailableDays([]))
      .finally(() => setLoadingDays(false));
  }, [activeMonth]);

  const fetchRanges = (date: Date) => {
    const d = dayjs(date).tz(TIME_ZONE).format('YYYY-MM-DD');
    setLoadingRanges(true);
    fetch(`/api/appointment/slots?date=${d}`)
      .then((res) => res.json())
      .then((data: { ranges?: TimeRangeValue[] }) => {
        setRanges(data.ranges ?? []);
      })
      .catch(() => setRanges([]))
      .finally(() => setLoadingRanges(false));
  };

  const handleDateChange = (value: CalendarValue) => {
    setError(null);
    setSuccess(false);
    setSelectedRange(null);
    if (value instanceof Date) {
      setSelectedDate(value);
      fetchRanges(value);
    } else {
      setSelectedDate(null);
      setRanges([]);
    }
  };

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') {
      return '';
    }
    const d = dayjs(date).tz(TIME_ZONE);
    if (d.day() === 0 || d.day() === 6) {
      return classes.sunday;
    }
    if (d.isBefore(dayjs().tz(TIME_ZONE).startOf('day'))) {
      return classes.past;
    }
    if (availableDays.includes(d.format('YYYY-MM-DD'))) {
      return classes.available;
    }
    return '';
  };

  const slots = useMemo<TimeRangeValue[]>(() => {
    if (!selectedDate) {
      return [];
    }
    const list: TimeRangeValue[] = [];
    DAILY_CANDIDATE_SLOTS.forEach(({ hour, minute }) => {
      const start = dayjs(selectedDate)
        .tz(TIME_ZONE)
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);
      const end = start.add(SLOT_MINUTES, 'minute');
      if (slotFitsRanges(start, end, ranges)) {
        list.push({ start: start.toISOString(), end: end.toISOString() });
      }
    });
    return list;
  }, [ranges, selectedDate]);

  const slotGroups = useMemo(() => {
    const groups: {
      morning: TimeRangeValue[];
      afternoon: TimeRangeValue[];
      evening: TimeRangeValue[];
    } = {
      morning: [],
      afternoon: [],
      evening: [],
    };
    slots.forEach((slot) => {
      const hour = dayjs(slot.start).tz(TIME_ZONE).hour();
      if (hour < 12) {
        groups.morning.push(slot);
      } else if (hour < 18) {
        groups.afternoon.push(slot);
      } else {
        groups.evening.push(slot);
      }
    });
    return groups;
  }, [slots]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRange) {
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const res = await fetch('/api/appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          company,
          lang,
          start: selectedRange.start,
          end: selectedRange.end,
          message,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t.error);
        gaFormSubmit('appointment', false);
        setSubmitting(false);
        return;
      }
      setSuccess(true);
      gaFormSubmit('appointment', true);
    } catch {
      setError(t.error);
      gaFormSubmit('appointment', false);
      setSubmitting(false);
      return;
    }

    setName('');
    setEmail('');
    setCompany('');
    setMessage('');
    setSelectedRange(null);

    setSubmitting(false);
  };

  const minDateString = useMemo(() => dayjs().tz(TIME_ZONE).format('YYYY-MM-DD'), []);

  const formattedSelectedDate = useMemo(() => {
    if (!selectedDate) {
      return '';
    }
    return selectedDate.toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: TIME_ZONE,
    });
  }, [selectedDate, lang]);

  const renderSlotGroup = (label: string, groupSlots: TimeRangeValue[]) => {
    if (groupSlots.length === 0) {
      return null;
    }
    return (
      <div key={label} className={classes.slotGroup}>
        <span className={classes.slotGroupLabel}>{label}</span>
        <div className={classes.slotsGrid}>
          {groupSlots.map((slot) => {
            const selected = selectedRange?.start === slot.start;
            return (
              <button
                key={slot.start}
                type="button"
                onClick={() => {
                  setSelectedRange(slot);
                  gaClick('appointment_slot_select', {
                    date: selectedDate?.toISOString() ?? '',
                    time: formatTime(slot.start, lang),
                  });
                }}
                className={`${classes.slotButton} ${selected ? classes.slotSelected : ''}`}
                aria-pressed={selected}
              >
                {formatTime(slot.start, lang)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.calendarRow}>
        <div className={classes.calendarCol}>
          <label htmlFor="apt-date" className={classes.dateLabel}>
            {t.dateLabel}
          </label>

          {isMobile ? (
            <input
              id="apt-date"
              type="date"
              min={minDateString}
              className={classes.dateInput}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  handleDateChange(new Date(`${value}T00:00:00`));
                } else {
                  handleDateChange(null);
                }
              }}
            />
          ) : (
            <Calendar
              value={selectedDate ?? undefined}
              onChange={handleDateChange}
              onActiveStartDateChange={({ activeStartDate }) => {
                if (activeStartDate) {
                  setActiveMonth(activeStartDate);
                }
              }}
              minDate={today}
              tileClassName={tileClassName}
              locale={isEn ? 'en-GB' : 'fr-FR'}
            />
          )}
        </div>

        <div className={classes.slotsCol}>
          {selectedDate ? (
            <>
              <div className={classes.slotsHeader}>
                <Clock size={16} />
                <div className={classes.slotsHeaderText}>
                  <span className={classes.slotsDate}>{formattedSelectedDate}</span>
                  <span className={classes.slotsLabel}>{t.slotsLabel}</span>
                </div>
              </div>

              {loadingRanges ? (
                <div className={classes.loader}>
                  <Loader2 size={22} className={classes.spinner} />
                </div>
              ) : slots.length > 0 ? (
                <div className={classes.slotGroups}>
                  {renderSlotGroup(t.morningLabel, slotGroups.morning)}
                  {renderSlotGroup(t.afternoonLabel, slotGroups.afternoon)}
                  {renderSlotGroup(t.eveningLabel, slotGroups.evening)}
                </div>
              ) : (
                <p className={classes.empty}>{t.noSlots}</p>
              )}
            </>
          ) : (
            <div className={classes.slotsPlaceholder}>
              <Clock size={28} />
              <p>{t.slotsPrompt}</p>
            </div>
          )}
        </div>
      </div>

      <form
        id="appointment-form"
        data-form-type="appointment"
        onSubmit={handleSubmit}
        className={classes.form}
      >
        <div className={classes.formGrid}>
          <div className={classes.field}>
            <label htmlFor="apt-name" className={classes.label}>
              {t.nameLabel}
            </label>
            <input
              id="apt-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={classes.input}
              placeholder={t.namePlaceholder}
            />
          </div>

          <div className={classes.field}>
            <label htmlFor="apt-company" className={classes.label}>
              {t.companyLabel}
            </label>
            <input
              id="apt-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className={classes.input}
              placeholder={t.companyPlaceholder}
            />
          </div>

          <div className={classes.field}>
            <label htmlFor="apt-email" className={classes.label}>
              {t.emailLabel}
            </label>
            <input
              id="apt-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={classes.input}
              placeholder={t.emailPlaceholder}
            />
          </div>
        </div>

        <div className={classes.field}>
          <label htmlFor="apt-message" className={classes.label}>
            {t.messageLabel}
          </label>
          <textarea
            id="apt-message"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={classes.textarea}
            placeholder={t.messagePlaceholder}
          />
        </div>

        <button
          type="submit"
          disabled={!selectedRange || submitting || success}
          className={classes.submitButton}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className={classes.spinner} />
              {t.submitting}
            </>
          ) : (
            t.submit
          )}
        </button>

        {success && (
          <div className={`${classes.status} ${classes.success}`}>
            <CheckCircle2 size={18} />
            <span>{t.success}</span>
          </div>
        )}
        {error && <div className={`${classes.status} ${classes.error}`}>{error}</div>}
      </form>

      <p className={classes.discreetNote}>{t.ndaNote}</p>
    </div>
  );
}
